import gql from 'graphql-tag';
import KnowledgeMapDownload, {
  workspaceknowledgeMapListFragments,
} from 'builder/Knowledge/queries/getDocument';
import { dispatch } from 'pluginServices/store';
import { pushToast } from 'containers/ToastManager/actions';
import KnowledgeMapCreate from 'builder/Knowledge/queries/competencyDocumentCreate';
import { updateCompetencyDocumentGraphqlCache } from 'Workspace/Knowledge/utils/graphqlCache';
import KnowledgeMapDelete from 'builder/Knowledge/queries/competencyDocumentDelete';
import CompetencyDocumentItemDelete from 'builder/Knowledge/queries/competencyDocumentItemDelete';
import linkKnowkedgeObjectives from 'builder/Knowledge/queries/linkObjectives';
import unlinkKnowkedgeObjectives from 'builder/Knowledge/queries/unlinkObjectives';
import allWorkspaceDocumentsList from 'builder/Knowledge/queries/getAllWorkspaceDocuments';
import workspaceDocumentsList from 'Workspace/queries/getDocumentsList';
import {
  queryObjectType,
  competencyItemGraphqlCache,
} from 'constants/knowledgeMap';

/**
 * get knowledge map data from graphql
 *
 * @param {string} workspaceId
 * @param {string} documentId
 * @param {Object} apolloClient - apollo client current instance.
 * @return {Object} - knowledge map document.
 */
export const getKnowledgeMapDetail = async (workspaceId, documentId, apolloClient) => {
  const response = await apolloClient.query({
    query: KnowledgeMapDownload,
    variables: {
      workspaceId,
      documentId,
    },
    errorPolicy: 'all',
  });

  if (response.errors) {
    throw new Error(response.errors);
  }

  return response;
};

/**
 * get knowledge map object from the response object
 * @param {Object} createCompetencyResponse - knowledge map response object.
 * @returns {Object} - knowledge map object with required fields.
 */
const getKnowledgeMapCreated = (createCompetencyResponse) => {
  const {
    data : {
      competencyDocumentCreate : {
        document: knowledgeMap,
      },
    },
  } = createCompetencyResponse;

  if(!knowledgeMap) {
    return undefined;
  }

  const { documentId, title, __typename } = knowledgeMap;
  return { id: documentId, title, __typename };
};

/**
 * update knowledge map cache list
 *
 * @param {string} workspaceId
 * @param {Object} client - apollo client instance.
 * @param {Object} createCompetencyResponse - knowledge map created.
 */
export const updateKnowledgeMapList = (deletedDocID, workspaceId, client, createCompetencyResponse) => {
  const { WORKSPACE,
    DOCUMENT_EDGE } = queryObjectType;
  const id = `${WORKSPACE}:${workspaceId}`;
  const { workspace: workspaceFragment } = workspaceknowledgeMapListFragments;

  //read knowledge map list in the workspace from graphql cache.
  const workspaceKnowledgeMapList = client.readFragment({
    id,
    fragment: workspaceFragment,
  });

  const knowledgeMapListEdges = workspaceKnowledgeMapList
    && workspaceKnowledgeMapList.competencyDocuments
    && workspaceKnowledgeMapList.competencyDocuments.edges;

  //append newly added knowledge map to the knowledge list edges.
  if (deletedDocID === '') {
    //newly created knowledge map.
    const knowledgeMapCreated = getKnowledgeMapCreated(createCompetencyResponse);
    if (!knowledgeMapListEdges || !knowledgeMapCreated) {
      return;
    }
    knowledgeMapListEdges.push({node: knowledgeMapCreated, __typename: DOCUMENT_EDGE});
  } else {
    workspaceKnowledgeMapList.competencyDocuments.edges = knowledgeMapListEdges.filter((element) => element.node.id !== deletedDocID);
  }

  //update graphql cache with updated knowledge map list.
  client.writeFragment({
    id,
    fragment: workspaceFragment,
    data: {
      ...workspaceKnowledgeMapList,
      __typename: workspaceKnowledgeMapList.__typename,
    },
  });
};

/*
 * @param {Object} responseErrors
 */
export const handleResponseErrors = (responseErrors) => {
  dispatch(pushToast({
    message: responseErrors,
  }));
};

/*
 * @param {number} workspaceId
 * @param {string} knowledgeMapName
 * @param {Object} apolloClient - apollo client current instance.
 * @returns {Object} - response of the graphql mutation
 */
export const createKnowledgeMap = async (workspaceId, knowledgeMapName, apolloClient) => {
  const response = await apolloClient.mutate({
    mutation: KnowledgeMapCreate,
    update: (...args) => updateKnowledgeMapList('', workspaceId, ...args),
    variables: {
      "workspaceId" : workspaceId,
      "title" : knowledgeMapName,
    },
    awaitRefetchQueries : true,
    errorPolicy: 'all',
  });

  const { errors: responseErrors } = response;
  if (responseErrors) {
    handleResponseErrors(responseErrors);
  }
  return response.data.competencyDocumentCreate.document;
};

/**
 *
 * @param {string} KMItemInsertQuery
 * @param {Object} apolloClient - apollo client current instance.
 * @returns {Object} - response of the graphql mutation
 */
export const createKnowledgeMapItem = async (knowledgeMapItemInsertQuery, apolloClient, competencyDocumentId) => {
  const response = await apolloClient.mutate({
    mutation: gql`
      mutation {
        ${knowledgeMapItemInsertQuery}
      }`,
    update: (...args) => updateCompetencyDocumentGraphqlCache(
      competencyDocumentId, 
      competencyItemGraphqlCache.COMPETENCY_ITEM_LIST_UPDATE,
      ...args
    ),
    awaitRefetchQueries : true,
    errorPolicy: 'all',
  });

  const { errors: responseErrors }= response;
  if (responseErrors) {
    handleResponseErrors(responseErrors);
  }
  return response;
};

/**
 *
 * @param {string} knowledgeMapItemsDeleteQuery
 * @param {Object} apolloClient - apollo client current instance.
 * @returns {Object} - response of the graphql mutation
 */
export const deleteAllKnowledgeMapItemsService = async (knowledgeMapItemsDeleteQuery, apolloClient, competencyDocumentId) => {
  const response = await apolloClient.mutate({
    mutation: gql`
      mutation {
        ${knowledgeMapItemsDeleteQuery}
      }`,
    update: (...args) => updateCompetencyDocumentGraphqlCache(
      competencyDocumentId, 
      competencyItemGraphqlCache.COMPETENCY_ITEM_DELETE_ALL,
      ...args
    ),
    awaitRefetchQueries : true,
    errorPolicy: 'all',
  });

  const { errors: responseErrors }= response;
  if (responseErrors) {
    handleResponseErrors(responseErrors[0].message);
  }
  return response;
};

/**
 *
 * @param {string} KMItemAssociationInsertQuery
 * @param {Object} apolloClient - apollo client current instance.
 * @returns {Object} - response of the graphql mutation
 */
export const createKnowledgeMapItemAssociation = async (
  knowledgeMapItemAssociationInsertQuery,
  apolloClient,
  competencyDocumentId
) => {
  const response = await apolloClient.mutate({
    mutation: gql`
    mutation {
      ${knowledgeMapItemAssociationInsertQuery}
    }`
    ,
    update: (...args) => updateCompetencyDocumentGraphqlCache(
      competencyDocumentId, 
      competencyItemGraphqlCache.COMPETENCY_ITEM_ASSOCIATION_UPDATE,
      ...args
    ),
    awaitRefetchQueries : true,
    errorPolicy: 'all',
  });

  const { errors: responseErrors }= response;
  if (responseErrors) {
    handleResponseErrors(responseErrors);
  }
  return response;
};

/**
 * delete knowledge map item
 *
 * @param {Object} competencyTree
 * @param {number} competencyDocumentId
 * @param {number} itemId
 * @param {Object} apolloClient - apollo client instance.
 * @returns {Object} createCompetencyResponse - knowledge map created.
 */
export const deleteCompetencyDocumentItem = async (
  competencyTree, 
  competencyDocumentId, 
  itemId, 
  apolloClient
) => {
  const response = await apolloClient.mutate({
    mutation: CompetencyDocumentItemDelete,
    update: (...args) => updateCompetencyDocumentGraphqlCache(
      competencyDocumentId, 
      competencyItemGraphqlCache.COMPETENCY_ITEM_DELETE , 
      ...args,
      { competencyTree }
    ),
    variables: {
      "documentId" : competencyDocumentId,
      "id" : itemId,
    },
    awaitRefetchQueries : true,
    errorPolicy: 'all',
  });

  const { errors: responseErrors }= response;
  if (responseErrors) {
    handleResponseErrors(responseErrors);
  }
  return response;
};

/* 
 * @param {number} workspaceId 
 * @param {number} documentId
 * @param {function} toast
 * @param {Object} apolloClient - apollo client current instance.
 * @returns {Object} - response of the graphql mutation
 */
export const deleteKnowledgeMap = async (workspaceId, documentId, toast, apolloClient) => {
  const response = await apolloClient.mutate({
    mutation: KnowledgeMapDelete,
    update: (...args) => updateKnowledgeMapList(documentId, workspaceId, ...args),
    variables: {
      "workspaceId" : workspaceId,
      "documentId" : documentId,
    },
    awaitRefetchQueries : true,
    errorPolicy: 'all',
  });

  const { errors: responseErrors }= response;
  if (responseErrors) {
    handleResponseErrors(responseErrors);
  }
  return response;
};

/**
 *
 * @param {string} knowledgeMapItemUpdateQuery
 * @param {Object} apolloClient - apollo client current instance.
 * @returns {Object} - response of the graphql mutation
 */
export const updateKnowledgeMapItem = async (knowledgeMapItemUpdateQuery, apolloClient, competencyDocumentId) => {
  const response = await apolloClient.mutate({
    mutation: gql`
    mutation {
      ${knowledgeMapItemUpdateQuery}
    }`,
    update: (...args) => updateCompetencyDocumentGraphqlCache(
      competencyDocumentId,
      competencyItemGraphqlCache.COMPETENCY_ITEM_LIST_REPLACE,
      ...args
    ),
    awaitRefetchQueries : true,
    errorPolicy: 'all',
  });
  const { errors: responseErrors }= response;
  if (responseErrors) {
    handleResponseErrors(responseErrors);
  }
  return response;
};
/*
 * @param {string} elementId
 * @param {string} elementType
 * @param {array} documentItems
 * @param {Object} apolloClient - apollo client current instance.
 * @returns {Object} - response of the graphql mutation
 */
export const linkObjectives = async (elementId, elementType, documentItems, apolloClient) => {
  const response = await apolloClient.mutate({
    mutation: linkKnowkedgeObjectives,
    variables: {
      elementId,
      elementType,
      documentItems,
    },
    awaitRefetchQueries : true,
    errorPolicy: 'all',
  });

  const { errors: responseErrors }= response;
  if (responseErrors) {
    handleResponseErrors(responseErrors);
  }
  return response;
};

/**
 *
 * @param {string} knowledgeMapItemDeleteQuery
 * @param {Object} apolloClient - apollo client current instance.
 * @param {string} competencyDocumentId
 * @returns {Object} - response of the graphql mutation
 */
export const deleteKnowledgeMapItemAssociation = async (knowledgeMapItemDeleteQuery, apolloClient, competencyDocumentId, associationObject) => {
  const response = await apolloClient.mutate({
    mutation: gql`
    mutation {
      ${knowledgeMapItemDeleteQuery}
    }`,
    update: (...args) => updateCompetencyDocumentGraphqlCache(
      competencyDocumentId,
      competencyItemGraphqlCache.COMPETENCY_ITEM_ASSOCIATION_REPLACE,
      ...args,
      { associationObject }
    ),
    awaitRefetchQueries : true,
    errorPolicy: 'all',
  });

  const { errors: responseErrors }= response;
  if (responseErrors) {
    handleResponseErrors(responseErrors);
  }
  return response;
};

/**
 * @param {string} elementId
 * @param {string} elementType
 * @param {array} documentItems
 * @param {Object} apolloClient - apollo client current instance.
 * @returns {Object} - response of the graphql mutation
 */
export const unlinkObjectives = async (elementId, elementType, documentItems, apolloClient) => {
  const response = await apolloClient.mutate({
    mutation: unlinkKnowkedgeObjectives,
    variables: {
      elementId,
      elementType,
      documentItems,
    },
    awaitRefetchQueries : true,
    errorPolicy: 'all',
  });

  const { errors: responseErrors }= response;
  if (responseErrors) {
    handleResponseErrors(responseErrors);
  }
  return response;
};

/**
 * get all the knowledge maps for a given workspace
 *
 * @param {string} workspaceId
 * @param {Object} apolloClient - apollo client current instance.
 * @return {Object} - knowledge map documents.
 */
export const getWorkspaceKnowledgeMaps = async (workspaceId, apolloClient) => {
  const response = await apolloClient.query({
    query: workspaceDocumentsList,
    variables: {
      workspaceId,
    },
    errorPolicy: 'all',
  });

  const { errors: responseErrors }= response;
  if (responseErrors) {
    handleResponseErrors(responseErrors);
  }
  return response;
};

/**
 * get all the knowledge maps for a across all the workspaces
 *
 * @param {Object} apolloClient - apollo client current instance.
 * @return {Object} - knowledge map documents.
 */
export const getAllWorkspaceKnowledgeMaps = async (apolloClient) => {
  const response = await apolloClient.query({
    query: allWorkspaceDocumentsList,
    errorPolicy: 'all',
  });

  const { errors: responseErrors }= response;
  if (responseErrors) {
    handleResponseErrors(responseErrors);
  }
  return response;
};
